// @flow

// Libs
import _get from 'lodash/get'
import _find from 'lodash/find'
import { createSelector } from 'reselect'
import moment from 'moment'

// Selectors
import { areaPerAsset$ } from 'fc-assets'
import { jobsFiltered$ } from 'hfty-soil-jobs'
import grids$ from 'hfty-grid/src/selectors/grids'
import { seasons$ } from 'fc-seasons'

// DataTable Composer
import composeDataTable from 'fc-component-datatable'

// Hocs
import soilJobsDataTableHoc from './hocs/SoilJobsDataTable'

// Constants
import {
  PROP_ASSET_ID,
  PROP_LABEL,
  PROP_SEASON,
} from 'fc-model-assets'
import {
  PROP_SOIL_JOB_ID,
  PROP_CREATED_DATE,
  PROP_FARM,
  PROP_FIELD,
  PROP_PRICE,
  PROP_STATUS,
  PROP_PAID,
} from 'fc-model-sampling-jobs'
import {
  DATATABLE_NAME,
  ATTR_SOIL_JOB_ID,
  ATTR_CREATED_DATE,
  ATTR_SEASON,
  ATTR_FARM,
  ATTR_FIELD,
  ATTR_GRID_SIZE,
  ATTR_AREA,
  ATTR_PRICE,
  ATTR_STATUS,
  ATTR_PAID,
  ATTR_HAS_MAP_VIEW,
  ATTR_HAS_LAB_RESULTS,
  ATTR_HAS_COMMENTS,
  ATTR_HAS_RECOMMENDATIONS,
  ATTR_HAS_REOPEN_JOB_BTN,
  NOT_PAID,
  UNIT_ABRV_HECTARES,
  UNIT_ABRV_ACRES,
  MULTI_GRID,
  NONE,
} from './constants'
import {
  PROP_ID,
  PROP_GRID,
  PROP_SOIL_JOB,
  GRID_UNIT_ACRES,
} from 'hfty-model-grid'
import {
  RESULTS_POSTED,
  ORDER_TEST,
} from 'hfty-model-soil-jobs'

// Types
import type { SoilJob } from 'hfty-model-soil-jobs/types'
import type { SoilJobRecord } from '../types'
import type {
  DataTableState,
  DataTableModule,
} from 'fc-component-datatable/types'
import type { Asset } from 'fc-model-assets/types'
import type { Grid } from 'hfty-model-grid/types'
import type { Season } from 'fc-model-seasons/types'

type ComposeSoilJobsDataTableArgs = {
  jobs$: (state: Object) => Array<SoilJob>,
  grids$: (state: Object) => Array<Grid>,
  farms$: (state: Object) => Array<Asset>,
  fields$: (state: Object) => Array<Asset>,
  tableState$: (state: Object) => DataTableState,
}

// Exports
export default function composeSoilJobsDataTable ({
  jobs$,
  farms$,
  fields$,
  tableState$,
  }: ComposeSoilJobsDataTableArgs): DataTableModule<SoilJob, SoilJobRecord> {
  //
  function makeRecords (records$: (state: Object) => Array<SoilJob>): (state: Object) => Array<SoilJobRecord> {
    return createSelector(
      [records$, seasons$, farms$, fields$, areaPerAsset$, grids$],
      function (
        jobs: Array<SoilJob>,
        seasons: Array<Season>,
        farms: Array<Asset>,
        fields: Array<Asset>,
        areaPerAsset: { [number]: number },
        grids: Array<Grid>,
      ): Array<SoilJobRecord> {
        console.warn('Recompute Records')

        return jobs.map((job: SoilJob): SoilJobRecord => {
          const farm: ?Asset = farms.find((farm: Asset): boolean => farm[PROP_ASSET_ID] === job[PROP_FARM])
          const field: ?Asset = fields.find((field: Asset): boolean => field[PROP_ASSET_ID] === job[PROP_FIELD])
          const grid: ?Grid = grids.find((grid: Grid): boolean => grid[PROP_SOIL_JOB] === job[PROP_ID])
          const parsedGrid = grid && grid[PROP_GRID] ? JSON.parse(grid[PROP_GRID]) : null
          const price = job[PROP_PAID] ? job[PROP_PRICE] : NOT_PAID
          let gridSize = NONE
          const season = field && _find(seasons, (season: Season): ?Object => field[PROP_SEASON] === season[PROP_ID])

          if (parsedGrid) {
            const { features = [] } = parsedGrid
            const gridUnit = _get(features, '[0].properties.units', GRID_UNIT_ACRES)
            const size = _get(features, '[0].properties.size', 0)
            const roundedSize = size.toFixed(2)
            const abrvUnit = gridUnit === GRID_UNIT_ACRES ? UNIT_ABRV_ACRES : UNIT_ABRV_HECTARES

            gridSize = features.length > 1 ? MULTI_GRID : `${roundedSize} ${abrvUnit}`
          }

          return {
            [ATTR_SOIL_JOB_ID]: job[PROP_SOIL_JOB_ID],
            [ATTR_CREATED_DATE]: moment(job[PROP_CREATED_DATE], 'X').format('DD MMM YY'),
            [ATTR_SEASON]: season ? season[PROP_LABEL] : null,
            [ATTR_FARM]: (farm && farm[PROP_LABEL]) || '',
            [ATTR_FIELD]: (field && field[PROP_LABEL]) || '',
            [ATTR_GRID_SIZE]: gridSize,
            [ATTR_AREA]: (field && areaPerAsset[field[PROP_ASSET_ID]]) || '',
            [ATTR_PRICE]: price,
            [ATTR_STATUS]: job[PROP_STATUS],
            [ATTR_PAID]: job[PROP_PAID],
            [ATTR_HAS_MAP_VIEW]: true,
            [ATTR_HAS_LAB_RESULTS]: job[PROP_STATUS] === RESULTS_POSTED,
            [ATTR_HAS_COMMENTS]: job[PROP_STATUS] === RESULTS_POSTED,
            [ATTR_HAS_RECOMMENDATIONS]: job[PROP_STATUS] === RESULTS_POSTED,
            [ATTR_HAS_REOPEN_JOB_BTN]: job[PROP_STATUS] === ORDER_TEST,
          }
        })
      }

    )
  }

  const datatable: DataTableModule<SoilJob, SoilJobRecord> = composeDataTable({name: DATATABLE_NAME})({
    data$: jobsFiltered$,
    tableState$,
    makeRecords,
  })

  return {
    ...datatable,
    DataTable: soilJobsDataTableHoc(datatable.DataTable),
  }
}
